import {
  LayoutGrid,
  Leaf,
  Package,
  ScanSearch,
  Smartphone,
  Snowflake,
  Sprout,
  Stethoscope,
  Sun,
  Sunrise,
  type LucideIcon,
} from "lucide-react"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import type { ComparisonEntry, GrassKey } from "@/data/comparisons"

// Shared icon + tint for a comparison card. Grass cards take their cue from
// the season pair (both warm = Sun amber, both cool = Snowflake sky-blue,
// mixed = Sunrise orange for the transition zone). Brand cards split three
// ways: app matchups (Smartphone indigo), seed (Sprout lime), and fertilizer
// (Package slate). Topic cards split by
// topicGroup: diagnosis (Stethoscope), visual ID (ScanSearch), and whole-yard
// surface decisions (LayoutGrid, the same glyph the artificial-turf calculator
// carries in data/tools-manifest.ts).
export function getComparisonIcon(c: ComparisonEntry): { Icon: LucideIcon; tint: string } {
  if (c.kind === "topic") {
    if (c.topicGroup === "diagnostic") {
      return { Icon: Stethoscope, tint: "bg-rose-100 text-rose-700" }
    }
    if (c.topicGroup === "lookalike") {
      return { Icon: ScanSearch, tint: "bg-violet-100 text-violet-700" }
    }
    return { Icon: LayoutGrid, tint: "bg-teal-100 text-teal-700" }
  }
  if (c.kind === "brand") {
    // App matchups first: brandCategoryOf() has nothing to say about a software
    // productType, so without this branch they would all fall through to the
    // generic Package glyph the fertilizer cards use.
    if (c.category === "app") {
      return { Icon: Smartphone, tint: "bg-indigo-100 text-indigo-700" }
    }
    const isSeed =
      /seed/i.test(c.a.productType) || /seed/i.test(c.b.productType)
    return isSeed
      ? { Icon: Sprout, tint: "bg-lime-100 text-lime-700" }
      : { Icon: Package, tint: "bg-slate-100 text-slate-700" }
  }
  const a = GRASS_CHARACTERISTICS[c.a.key]?.season
  const b = GRASS_CHARACTERISTICS[c.b.key]?.season
  if (a === "Warm" && b === "Warm") {
    return { Icon: Sun, tint: "bg-amber-100 text-amber-700" }
  }
  if (a === "Cool" && b === "Cool") {
    return { Icon: Snowflake, tint: "bg-sky-100 text-sky-700" }
  }
  return { Icon: Sunrise, tint: "bg-orange-100 text-orange-700" }
}

// Per-grass icon + tint. Used by single-grass cards (e.g. the "Go deeper on
// either grass" pillar-guide cards). Falls back to a neutral Leaf icon when
// the season can't be resolved.
export function getGrassIcon(key: GrassKey): {
  Icon: LucideIcon
  tint: string
  seasonLabel: string
} {
  const season = GRASS_CHARACTERISTICS[key]?.season
  if (season === "Warm") {
    return { Icon: Sun, tint: "bg-amber-100 text-amber-700", seasonLabel: "Warm season" }
  }
  if (season === "Cool") {
    return { Icon: Snowflake, tint: "bg-sky-100 text-sky-700", seasonLabel: "Cool season" }
  }
  return { Icon: Leaf, tint: "bg-emerald-100 text-emerald-700", seasonLabel: "" }
}
